import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { emailToUsername, formatDateTime } from '../utils/formatters';
import { useRBAC } from '../components/ProtectedRoute';
import './TestRunListPage.css';

interface TestRunSummary {
  id: string;
  name: string;
  folder_id: string;
  status: string;
  created_at: string;
  created_by: string;
  total_cases: number;
  pass_count: number;
  fail_count: number;
  not_run_count: number;
  progress_percentage: number;
  environment?: string;
  cycle_label?: string;
  folder_name?: string;   // direct folder (Story level)
  project_name?: string;  // root folder (Project level)
}

type TabFilter = 'Recent' | 'Scheduled' | 'Archived';
type SortOption = 'date-newest' | 'date-oldest' | 'pass-rate-high' | 'pass-rate-low' | 'cycle-az' | 'name-az';

// Environment badge colors
const ENV_COLORS: Record<string, { bg: string; color: string }> = {
  Stage:      { bg: '#dbeafe', color: '#1d4ed8' },
  Preprod:    { bg: '#fef9c3', color: '#854d0e' },
  PROD:       { bg: '#dcfce7', color: '#166534' },
  Production: { bg: '#dcfce7', color: '#166534' },
};

const TestRunListPage: React.FC = () => {
  const navigate = useNavigate();
  const { canWrite } = useRBAC();
  const [testRuns, setTestRuns] = useState<TestRunSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [activeTab, setActiveTab] = useState<TabFilter>('Recent');
  const [expandedCycles, setExpandedCycles] = useState<Set<string>>(new Set());

  // Cycle label editing
  const [editingCycle, setEditingCycle] = useState<string | null>(null);   // current label being edited
  const [editCycleValue, setEditCycleValue] = useState<string>('');
  const [savingCycle, setSavingCycle] = useState(false);
  const [cycleEditError, setCycleEditError] = useState<string | null>(null);

  // New filters
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [environmentFilter, setEnvironmentFilter] = useState<string>('');
  const [creatorFilter, setCreatorFilter] = useState<string>('');
  const [hasEvidenceFilter, setHasEvidenceFilter] = useState<boolean>(false);
  const [dateRangeStart, setDateRangeStart] = useState<string>('');
  const [dateRangeEnd, setDateRangeEnd] = useState<string>('');
  const [sortBy, setSortBy] = useState<SortOption>('date-newest');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState<boolean>(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    loadTestRuns();
  }, [statusFilter]);

  const loadTestRuns = async () => {
    try {
      setLoading(true);
      const runs = await api.listTestRuns(undefined, statusFilter || undefined);
      setTestRuns(runs);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to load test runs');
    } finally {
      setLoading(false);
    }
  };

  const toggleCycle = (label: string) => {
    setExpandedCycles(prev => {
      const next = new Set(prev);
      next.has(label) ? next.delete(label) : next.add(label);
      return next;
    });
  };

  // Target environment values match the canonical set used by Create Test
  // Run (CreateTestRunPage.tsx) — "Prod" here is just the display column's
  // label, the stored value is "PROD".
  const CLONE_ENV_VALUES: Record<string, string> = { Preprod: 'Preprod', Prod: 'PROD' };

  // Tracks which cycle+environment clone is currently in flight, e.g. "CB-5290:Prod"
  const [cloningKey, setCloningKey] = useState<string | null>(null);

  // Stage is always created manually via the full Create Test Run form —
  // never auto/clone-generated. Preprod/Prod can only be cloned FROM an
  // existing Stage run, and only once that Stage run exists.
  const handleCloneRun = async (
    e: React.MouseEvent,
    cycleLabel: string,
    targetEnv: 'Preprod' | 'Prod',
    cycleRuns: TestRunSummary[]
  ) => {
    e.stopPropagation();
    const stageRun = cycleRuns.find(r => r.environment === 'Stage');
    if (!stageRun) return;
    const key = `${cycleLabel}:${targetEnv}`;
    try {
      setCloningKey(key);
      setError(null);
      await api.cloneTestRun(stageRun.id, CLONE_ENV_VALUES[targetEnv]);
      await loadTestRuns();
    } catch (err: any) {
      setError(err?.response?.data?.detail || err.message || `Failed to create ${targetEnv} run`);
    } finally {
      setCloningKey(null);
    }
  };

  const startEditCycle = (e: React.MouseEvent, label: string) => {
    e.stopPropagation();
    setEditingCycle(label);
    setEditCycleValue(label);
    setCycleEditError(null);
  };

  const cancelEditCycle = (e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingCycle(null);
    setEditCycleValue('');
    setCycleEditError(null);
  };

  const saveCycleLabel = async (e: React.MouseEvent, oldLabel: string) => {
    e.stopPropagation();
    const newLabel = editCycleValue.trim();
    if (!newLabel || newLabel === oldLabel) {
      setEditingCycle(null);
      return;
    }
    setSavingCycle(true);
    setCycleEditError(null);
    try {
      await api.renameCycleLabel(oldLabel, newLabel);
      await loadTestRuns();
      // Keep the renamed cycle expanded
      setExpandedCycles(prev => {
        const next = new Set(prev);
        if (next.has(oldLabel)) { next.delete(oldLabel); next.add(newLabel); }
        return next;
      });
      setEditingCycle(null);
    } catch (err: any) {
      setCycleEditError(err.message || 'Failed to rename cycle');
    } finally {
      setSavingCycle(false);
    }
  };

  // Get unique environments and creators for filter dropdowns
  const uniqueEnvironments = Array.from(new Set(testRuns.map(r => r.environment).filter(Boolean))).sort();
  const uniqueCreators = Array.from(new Set(testRuns.map(r => r.created_by))).sort();

  // Apply all filters and sorting
  const applyFiltersAndSort = () => {
    let filtered = testRuns;

    // Search filter (name + cycle label)
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(run =>
        run.name.toLowerCase().includes(term) ||
        (run.cycle_label && run.cycle_label.toLowerCase().includes(term))
      );
    }

    // Status filter
    if (statusFilter) {
      filtered = filtered.filter(run => run.status === statusFilter);
    }

    // Environment filter
    if (environmentFilter) {
      filtered = filtered.filter(run => run.environment === environmentFilter);
    }

    // Creator filter
    if (creatorFilter) {
      filtered = filtered.filter(run => run.created_by === creatorFilter);
    }

    // Date range filter
    if (dateRangeStart) {
      const startDate = new Date(dateRangeStart).getTime();
      filtered = filtered.filter(run => new Date(run.created_at).getTime() >= startDate);
    }
    if (dateRangeEnd) {
      const endDate = new Date(dateRangeEnd).getTime();
      filtered = filtered.filter(run => new Date(run.created_at).getTime() <= endDate);
    }

    // Has evidence filter (check if any execution has evidence)
    if (hasEvidenceFilter) {
      // For now, we'll assume runs with COMPLETED status likely have evidence
      // This is a client-side heuristic; ideally backend would provide this
      filtered = filtered.filter(run => run.status === 'COMPLETED' && run.pass_count > 0);
    }

    // Apply sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'date-newest':
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
        case 'date-oldest':
          return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
        case 'pass-rate-high':
          return (b.pass_count / b.total_cases) - (a.pass_count / a.total_cases);
        case 'pass-rate-low':
          return (a.pass_count / a.total_cases) - (b.pass_count / b.total_cases);
        case 'cycle-az':
          return (a.cycle_label || '').localeCompare(b.cycle_label || '');
        case 'name-az':
          return a.name.localeCompare(b.name);
        default:
          return 0;
      }
    });

    return filtered;
  };

  const filteredRuns = applyFiltersAndSort();

  // Check if any filters are active
  const hasActiveFilters = searchTerm || statusFilter || environmentFilter || creatorFilter || dateRangeStart || dateRangeEnd || hasEvidenceFilter || sortBy !== 'date-newest';

  const clearAllFilters = () => {
    setSearchTerm('');
    setStatusFilter('');
    setEnvironmentFilter('');
    setCreatorFilter('');
    setDateRangeStart('');
    setDateRangeEnd('');
    setHasEvidenceFilter(false);
    setSortBy('date-newest');
  };

  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'NOT_STARTED':
        return { label: 'NOT STARTED', className: 'trl-status-not-started', dot: false };
      case 'IN_PROGRESS':
        return { label: 'IN PROGRESS', className: 'trl-status-running', dot: true };
      case 'COMPLETED':
        return { label: 'COMPLETED', className: 'trl-status-completed', dot: false };
      case 'ABORTED':
        return { label: 'ABORTED', className: 'trl-status-aborted', dot: false };
      default:
        return { label: status, className: 'trl-status-not-started', dot: false };
    }
  };

  const getProgressBarClass = (status: string, failCount: number) => {
    if (status === 'COMPLETED' && failCount > 0) return 'trl-progress-fill trl-progress-fail';
    if (status === 'COMPLETED') return 'trl-progress-fill trl-progress-success';
    if (status === 'IN_PROGRESS') return 'trl-progress-fill trl-progress-running';
    return 'trl-progress-fill trl-progress-empty';
  };

  const getActionLabel = (status: string) => {
    switch (status) {
      case 'COMPLETED': return 'View Results';
      case 'IN_PROGRESS': return 'Open Live Log';
      case 'NOT_STARTED': return 'Start Now';
      default: return 'View';
    }
  };

  const getActionClass = (status: string, failCount: number) => {
    if (status === 'COMPLETED' && failCount > 0) return 'trl-action-rerun';
    return 'trl-action-primary';
  };

  const getUserInitials = (email: string) => {
    const name = emailToUsername(email);
    return name.slice(0, 2).toUpperCase();
  };

  // Derive cycle status from child runs
  const getCycleStatus = (runs: TestRunSummary[]) => {
    if (runs.every(r => r.status === 'COMPLETED')) return 'COMPLETED';
    if (runs.some(r => r.status === 'IN_PROGRESS')) return 'IN_PROGRESS';
    if (runs.some(r => r.status === 'NOT_STARTED')) return 'NOT_STARTED';
    return runs[0]?.status || 'NOT_STARTED';
  };

  const renderRunCard = (run: TestRunSummary) => {
    const statusCfg = getStatusConfig(run.status);
    const actionLabel = getActionLabel(run.status);
    const actionClass = getActionClass(run.status, run.fail_count);
    const initials = getUserInitials(run.created_by);
    const username = emailToUsername(run.created_by);
    const envCfg = run.environment ? (ENV_COLORS[run.environment] || { bg: '#f3f4f6', color: '#374151' }) : null;

    return (
      <div key={run.id} className="trl-card" onClick={() => navigate(`/test-runs/${run.id}`)}>
        <div className="trl-card-top">
          <span className={`trl-status-badge ${statusCfg.className}`}>
            {statusCfg.dot && <span className="trl-status-dot" />}
            {statusCfg.label}
          </span>
          {envCfg && run.environment && (
            <span className="trl-env-badge" style={{ background: envCfg.bg, color: envCfg.color }}>
              {run.environment}
            </span>
          )}
        </div>
        <div className="trl-card-title-section">
          <h3 className="trl-card-name">{run.name}</h3>
          <p className="trl-card-date">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
            </svg>
            Created {formatDateTime(run.created_at)}
          </p>
        </div>
        <div className="trl-progress-section">
          <div className="trl-progress-header">
            <span className="trl-progress-label">Progress</span>
            <span className="trl-progress-pct">{run.progress_percentage.toFixed(0)}%</span>
          </div>
          <div className="trl-progress-track">
            <div className={getProgressBarClass(run.status, run.fail_count)} style={{ width: `${run.progress_percentage}%` }} />
          </div>
        </div>
        <div className="trl-metrics">
          <div className="trl-metric"><span className="trl-metric-label">TOTAL</span><span className="trl-metric-value">{run.total_cases}</span></div>
          <div className="trl-metric trl-metric-pass"><span className="trl-metric-label">PASS</span><span className="trl-metric-value">{run.pass_count}</span></div>
          <div className="trl-metric trl-metric-fail"><span className="trl-metric-label">FAIL</span><span className="trl-metric-value">{run.fail_count}</span></div>
          <div className="trl-metric trl-metric-skip">
            <span className="trl-metric-label">{run.status === 'IN_PROGRESS' ? 'LEFT' : 'SKIP'}</span>
            <span className="trl-metric-value">{run.not_run_count}</span>
          </div>
        </div>
        <div className="trl-card-footer">
          <div className="trl-user">
            <div className="trl-avatar">{initials}</div>
            <span className="trl-username">{username}</span>
          </div>
          <button className={`trl-action-btn ${actionClass}`} onClick={(e) => { e.stopPropagation(); navigate(`/test-runs/${run.id}`); }}>
            {actionLabel}
          </button>
        </div>
      </div>
    );
  };

  // Group runs: Project → Cycle → runs
  // Falls back gracefully: no project_name → "Other", no cycle_label → individual under project
  const projectGroups: Record<string, {
    cycles: Record<string, TestRunSummary[]>;
    uncycled: TestRunSummary[];
  }> = {};

  filteredRuns.forEach(run => {
    const project = (run.project_name || '').trim() || 'Other';
    if (!projectGroups[project]) projectGroups[project] = { cycles: {}, uncycled: [] };
    const pg = projectGroups[project];
    if (run.cycle_label && run.cycle_label.trim()) {
      const cycle = run.cycle_label.trim();
      if (!pg.cycles[cycle]) pg.cycles[cycle] = [];
      pg.cycles[cycle].push(run);
    } else {
      pg.uncycled.push(run);
    }
  });

  const [expandedProjects, setExpandedProjects] = useState<Set<string>>(new Set(Object.keys(projectGroups)));

  const toggleProject = (project: string) => {
    setExpandedProjects(prev => {
      const next = new Set(prev);
      next.has(project) ? next.delete(project) : next.add(project);
      return next;
    });
  };

  if (loading) {
    return <div className="trl-page"><div className="trl-loading">Loading test runs...</div></div>;
  }

  return (
    <div className="trl-page">
      {/* Page Header */}
      <div className="trl-header">
        <div className="trl-header-left">
          <span className="trl-eyebrow">QUALITY ASSURANCE HUB</span>
          <h1 className="trl-title">Test Runs</h1>
        </div>
        {canWrite && (
          <button className="trl-btn-create" onClick={() => navigate('/test-runs/create')}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/>
            </svg>
            Create Test Run
          </button>
        )}
      </div>

      {error && <div className="trl-error">{error}</div>}

      {/* Minimalist Search and Filter Bar */}
      <div className="trl-controls">
        <div className="trl-search-box">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <input
            type="text"
            placeholder="Search runs..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="trl-filter-controls">
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="trl-mini-select">
            <option value="">All Status</option>
            <option value="NOT_STARTED">Not Started</option>
            <option value="IN_PROGRESS">In Progress</option>
            <option value="COMPLETED">Completed</option>
            <option value="ABORTED">Aborted</option>
          </select>

          <select value={environmentFilter} onChange={(e) => setEnvironmentFilter(e.target.value)} className="trl-mini-select">
            <option value="">All Env</option>
            {uniqueEnvironments.map(env => (
              <option key={env} value={env}>{env}</option>
            ))}
          </select>

          <select value={sortBy} onChange={(e) => setSortBy(e.target.value as SortOption)} className="trl-mini-select">
            <option value="date-newest">Newest</option>
            <option value="date-oldest">Oldest</option>
            <option value="pass-rate-high">Pass ↓</option>
            <option value="pass-rate-low">Pass ↑</option>
            <option value="name-az">Name A-Z</option>
          </select>

          <button
            className={`trl-filter-btn${showAdvancedFilters ? ' active' : ''}`}
            onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
            title="More filters"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
            </svg>
            {hasActiveFilters && <span className="trl-dot">●</span>}
          </button>

          {hasActiveFilters && (
            <button className="trl-clear-btn" onClick={clearAllFilters}>
              ✕
            </button>
          )}
        </div>
      </div>

      {/* Advanced Filters Dropdown */}
      {showAdvancedFilters && (
        <div className="trl-advanced-panel">
          <div className="trl-adv-row">
            <div className="trl-adv-col">
              <label>Created By</label>
              <select value={creatorFilter} onChange={(e) => setCreatorFilter(e.target.value)} className="trl-adv-select">
                <option value="">All Users</option>
                {uniqueCreators.map(creator => (
                  <option key={creator} value={creator}>{emailToUsername(creator)}</option>
                ))}
              </select>
            </div>

            <div className="trl-adv-col">
              <label>From Date</label>
              <input
                type="date"
                value={dateRangeStart}
                onChange={(e) => setDateRangeStart(e.target.value)}
                className="trl-adv-input"
              />
            </div>

            <div className="trl-adv-col">
              <label>To Date</label>
              <input
                type="date"
                value={dateRangeEnd}
                onChange={(e) => setDateRangeEnd(e.target.value)}
                className="trl-adv-input"
              />
            </div>

            <div className="trl-adv-col trl-adv-checkbox">
              <label>
                <input
                  type="checkbox"
                  checked={hasEvidenceFilter}
                  onChange={(e) => setHasEvidenceFilter(e.target.checked)}
                />
                Has Evidence
              </label>
            </div>
          </div>
        </div>
      )}

      {/* Tabs and Count */}
      <div className="trl-toolbar">
        <div className="trl-tabs">
          {(['Recent', 'Scheduled', 'Archived'] as TabFilter[]).map(tab => (
            <button key={tab} className={`trl-tab ${activeTab === tab ? 'trl-tab-active' : ''}`} onClick={() => setActiveTab(tab)}>{tab}</button>
          ))}
        </div>
        <div className="trl-count-label">
          {filteredRuns.length} of {testRuns.length}
        </div>
      </div>

      {/* Grid */}
      {filteredRuns.length === 0 ? (
        <div className="trl-empty">
          <p>No test runs found. Create your first test run to get started.</p>
          {canWrite && <button className="trl-btn-create" onClick={() => navigate('/test-runs/create')}>+ Create Test Run</button>}
        </div>
      ) : (
        <div className="trl-grid">
          {/* Project → Cycle → runs hierarchy */}
          {Object.entries(projectGroups).map(([projectName, pg]) => {
            const isProjExpanded = expandedProjects.has(projectName);
            const allProjectRuns = [
              ...Object.values(pg.cycles).flat(),
              ...pg.uncycled,
            ];
            const projStatus = getCycleStatus(allProjectRuns);
            const projStatusCfg = getStatusConfig(projStatus);
            const projPass = allProjectRuns.reduce((s, r) => s + r.pass_count, 0);
            const projFail = allProjectRuns.reduce((s, r) => s + r.fail_count, 0);
            const projTotal = allProjectRuns.reduce((s, r) => s + r.total_cases, 0);
            const projProgress = allProjectRuns.length > 0
              ? allProjectRuns.reduce((s, r) => s + r.progress_percentage, 0) / allProjectRuns.length
              : 0;

            return (
              <div key={projectName} className="trl-project-wrapper">
                {/* Project header */}
                <div className="trl-project-header" onClick={() => toggleProject(projectName)}>
                  <div className="trl-project-left">
                    <svg className={`trl-cycle-chevron ${isProjExpanded ? 'expanded' : ''}`} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <polyline points="9 18 15 12 9 6"/>
                    </svg>
                    <div>
                      <div className="trl-project-label">PROJECT</div>
                      <div className="trl-project-name">{projectName}</div>
                    </div>
                  </div>
                  <div className="trl-cycle-right">
                    <span className={`trl-status-badge ${projStatusCfg.className}`}>
                      {projStatusCfg.dot && <span className="trl-status-dot" />}
                      {projStatusCfg.label}
                    </span>
                    <span className="trl-cycle-run-count">
                      {allProjectRuns.length} run{allProjectRuns.length !== 1 ? 's' : ''}
                    </span>
                  </div>
                </div>

                {isProjExpanded && (
                  <div className="trl-project-body">
                    {/* Cycles under this project */}
                    {Object.entries(pg.cycles).map(([cycleLabel, cycleRuns]) => {
                      const isExpanded = expandedCycles.has(cycleLabel);
                      const cycleStatus = getCycleStatus(cycleRuns);
                      const statusCfg = getStatusConfig(cycleStatus);
                      const totalPass = cycleRuns.reduce((s, r) => s + r.pass_count, 0);
                      const totalFail = cycleRuns.reduce((s, r) => s + r.fail_count, 0);
                      const totalCases = cycleRuns.reduce((s, r) => s + r.total_cases, 0);
                      const avgProgress = cycleRuns.length > 0
                        ? cycleRuns.reduce((s, r) => s + r.progress_percentage, 0) / cycleRuns.length
                        : 0;
                      const envs = [...new Set(cycleRuns.map(r => r.environment).filter(Boolean))];

                      return (
                        <div key={cycleLabel} className="trl-cycle-wrapper trl-cycle-nested">
                          <div className="trl-cycle-card" onClick={() => !editingCycle && toggleCycle(cycleLabel)}>
                            <div className="trl-cycle-card-top">
                              <div className="trl-cycle-left">
                                <svg className={`trl-cycle-chevron ${isExpanded ? 'expanded' : ''}`} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                  <polyline points="9 18 15 12 9 6"/>
                                </svg>
                                <div>
                                  <div className="trl-cycle-label">
                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: 5 }}>
                                      <path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"/>
                                    </svg>
                                    CYCLE
                                  </div>
                                  {editingCycle === cycleLabel ? (
                                    <div className="trl-cycle-edit-row" onClick={e => e.stopPropagation()}>
                                      <input className="trl-cycle-edit-input" value={editCycleValue} onChange={e => setEditCycleValue(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') saveCycleLabel(e as any, cycleLabel); if (e.key === 'Escape') cancelEditCycle(e as any); }} autoFocus disabled={savingCycle} />
                                      <button className="trl-cycle-edit-save" onClick={e => saveCycleLabel(e, cycleLabel)} disabled={savingCycle || !editCycleValue.trim()} title="Save">{savingCycle ? '…' : '✓'}</button>
                                      <button className="trl-cycle-edit-cancel" onClick={cancelEditCycle} disabled={savingCycle} title="Cancel">✕</button>
                                      {cycleEditError && <span className="trl-cycle-edit-error">{cycleEditError}</span>}
                                    </div>
                                  ) : (
                                    <div className="trl-cycle-name-row">
                                      <div className="trl-cycle-name">{cycleLabel}</div>
                                      {canWrite && (
                                        <button className="trl-cycle-edit-btn" onClick={e => startEditCycle(e, cycleLabel)} title="Rename cycle">
                                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                                        </button>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div className="trl-cycle-right">
                                {/* 3-column environment status grid */}
                                <div className="trl-env-grid">
                                  {(() => { const stageExists = cycleRuns.some(r => r.environment === 'Stage'); return (['Stage', 'Preprod', 'Prod'] as const).map(env => {
                                    const run = cycleRuns.find(r =>
                                      r.environment === env || r.environment?.toLowerCase() === env.toLowerCase()
                                    );
                                    const dotColor = !run
                                      ? '#d1d5db'
                                      : run.status === 'COMPLETED'   ? '#22c55e'
                                      : run.status === 'IN_PROGRESS' ? '#f59e0b'
                                      : run.status === 'ABORTED'     ? '#ef4444'
                                      : '#9ca3af'; // NOT_STARTED

                                    const statusLabel = !run
                                      ? 'Not Created'
                                      : run.status === 'COMPLETED'   ? 'Completed'
                                      : run.status === 'IN_PROGRESS' ? 'In Progress'
                                      : run.status === 'ABORTED'     ? 'Aborted'
                                      : 'Not Started';

                                    const envColors: Record<string, string> = {
                                      Stage: '#1d4ed8', Preprod: '#92400e', Prod: '#166534'
                                    };

                                    const isCloning = cloningKey === `${cycleLabel}:${env}`;

                                    return (
                                      <div key={env} className="trl-env-col">
                                        <div className="trl-env-col-top">
                                          <span className="trl-env-dot" style={{ background: dotColor }} />
                                          <span className="trl-env-col-name" style={{ color: envColors[env] }}>{env}</span>
                                        </div>
                                        <span className={`trl-env-col-status ${!run ? 'trl-env-col-missing' : ''}`}>
                                          {statusLabel}
                                        </span>
                                        {!run && canWrite && env !== 'Stage' && stageExists && (
                                          <button
                                            className="trl-env-create-btn"
                                            disabled={isCloning}
                                            onClick={(e) => handleCloneRun(e, cycleLabel, env, cycleRuns)}
                                            title={`Create a ${env} run with the same scenarios/test cases as the Stage run`}
                                          >
                                            {isCloning ? '…' : `+ Create ${env}`}
                                          </button>
                                        )}
                                      </div>
                                    );
                                  }); })()}
                                </div>
                              </div>
                            </div>
                            <div className="trl-cycle-metrics">
                              <div className="trl-cycle-progress-track">
                                <div className="trl-progress-fill trl-progress-running" style={{ width: `${avgProgress.toFixed(0)}%` }} />
                              </div>
                              <span className="trl-cycle-progress-pct">{avgProgress.toFixed(0)}% avg</span>
                              <span className="trl-cycle-stat trl-cycle-pass">✓ {totalPass}</span>
                              <span className="trl-cycle-stat trl-cycle-fail">✗ {totalFail}</span>
                              <span className="trl-cycle-stat">/ {totalCases} total</span>
                            </div>
                          </div>
                          {isExpanded && (
                            <div className="trl-cycle-children">
                              {cycleRuns.map(run => renderRunCard(run))}
                            </div>
                          )}
                        </div>
                      );
                    })}

                    {/* Uncycled runs under this project */}
                    {pg.uncycled.length > 0 && (
                      <div className="trl-uncycled-group">
                        {pg.uncycled.map(run => renderRunCard(run))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          {/* Promo card */}
          <div className="trl-card trl-card-promo">
            <div className="trl-promo-icon">
              <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="1.5">
                <polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>
              </svg>
            </div>
            <h3 className="trl-promo-title">Need a specific run?</h3>
            <p className="trl-promo-text">Use a Cycle Label to group Stage / Preprod / Production runs together.</p>
            <button className="trl-promo-btn" onClick={() => navigate('/test-runs/create')}>Quick Start Guide</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TestRunListPage;
